import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';

// Claves VAPID generadas para Citados
const VAPID_PUBLIC_KEY = 'BOU5KteUuvK2XONQqxXNGYWHRzgrgl7XW9Eo32HunKF04dZ-a7vukG416uS4gSrR6L5IibX-u7xRcLdNSIhMQNQ';

// ─── Detección iOS PWA ───
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isIOSPWA = () => isIOS() && (window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches);
const isPushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

// Helper: verificar si la API Notification existe
const getNotificationPermission = () => {
  if (typeof window !== 'undefined' && 'Notification' in window) {
    return Notification.permission;
  }
  return 'unsupported';
};

// ─── Conversión ArrayBuffer → Base64 URL-safe (compatible con web-push) ───
function arrayBufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export const usePushNotifications = (user) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [permission, setPermission] = useState(getNotificationPermission());
  const [iosStatus, setIosStatus] = useState('unknown'); // 'unknown' | 'safari' | 'pwa' | 'supported'

  // Detectar entorno iOS al montar
  useEffect(() => {
    if (isIOS()) {
      setIosStatus(isIOSPWA() ? 'pwa' : 'safari');
    } else if (isPushSupported()) {
      setIosStatus('supported');
    } else {
      setIosStatus('unsupported');
    }
  }, []);

  // Check subscription al montar + auto-subscribe para PWA instaladas
  useEffect(() => {
    if (!user) return;
    checkSubscription();
  }, [user]);

  const checkSubscription = useCallback(async () => {
    if (!isPushSupported()) {
      console.log('[Push] API no soportada en este dispositivo/navegador');
      return;
    }
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();
      
      if (sub) {
        setIsSubscribed(true);
        setSubscription(sub);
        console.log('[Push] Suscripción existente detectada:', sub.endpoint.substring(0, 50) + '...');
      } else {
        setIsSubscribed(false);
        setSubscription(null);
        
        // Auto-subscribe si es PWA instalada y ya tiene permiso granted
        if (isIOSPWA() && Notification.permission === 'granted') {
          console.log('[Push] PWA detectada con permiso granted — auto-subscribing...');
          await subscribeUser();
        }
      }
    } catch (err) {
      console.error('[Push] Error al verificar suscripción:', err);
    }
  }, [user]);

  const subscribeUser = useCallback(async () => {
    // Bloquear en iOS Safari (no-PWA) con mensaje claro
    if (isIOS() && !isIOSPWA()) {
      console.warn('[Push] iOS Safari no soporta push. Requiere instalar como PWA.');
      return false;
    }
    
    if (!isPushSupported()) {
      console.warn('[Push] Push messaging no soportado en este dispositivo');
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Solicitar permiso
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') {
        console.warn('[Push] Permiso denegado por el usuario');
        return false;
      }

      // Crear suscripción push
      const sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // Extraer keys en formato Base64 URL-safe (requerido por web-push)
      const authKey = arrayBufferToBase64Url(sub.getKey('auth'));
      const p256dhKey = arrayBufferToBase64Url(sub.getKey('p256dh'));

      console.log('[Push] Guardando suscripción en DB:', {
        endpoint: sub.endpoint.substring(0, 50) + '...',
        authKey: authKey.substring(0, 10) + '...',
        p256dhKey: p256dhKey.substring(0, 10) + '...'
      });

      // Guardar en Supabase
      const { error } = await supabase
        .from('citados_push_subscriptions')
        .upsert({
          user_id: user.id,
          endpoint: sub.endpoint,
          auth_key: authKey,
          p256dh_key: p256dhKey
        }, { onConflict: 'user_id, endpoint' });

      if (error) {
        console.error('[Push] Error al guardar suscripción en DB:', error);
        throw error;
      }

      setIsSubscribed(true);
      setSubscription(sub);
      console.log('[Push] ✅ Suscripción activa exitosamente');
      return true;
    } catch (err) {
      console.error('[Push] Error al suscribirse:', err);
      return false;
    }
  }, [user]);

  const unsubscribeUser = useCallback(async () => {
    if (subscription) {
      try {
        await subscription.unsubscribe();
        
        // Eliminar de Supabase
        await supabase
          .from('citados_push_subscriptions')
          .delete()
          .eq('endpoint', subscription.endpoint);
          
        setIsSubscribed(false);
        setSubscription(null);
        console.log('[Push] Suscripción eliminada correctamente');
      } catch (err) {
        console.error('[Push] Error al desuscribirse:', err);
      }
    }
  }, [subscription]);

  return { 
    isSubscribed, 
    subscribeUser, 
    unsubscribeUser, 
    permission,
    iosStatus  // 'safari' | 'pwa' | 'supported' | 'unsupported'
  };
};

// Utilidad para convertir la clave VAPID (URL-safe Base64 → Uint8Array)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
